import { useEffect, useRef, useState } from "react";
import {
  ArrowClockwise,
  Copy,
  House,
  WarningCircle,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { isRouteErrorResponse } from "react-router-dom";

import BrandLogo from "@/components/common/BrandLogo";
import { toast } from "@/lib/toast";
import {
  primaryButtonStyles,
  secondaryButtonStyles,
} from "@/features/product/styles/product";
import { cn } from "@/utils/cn";

type ErrorPageProps = {
  error?: unknown;
  onRetry?: () => void;
};

type ErrorSummary = {
  status: string;
  detail?: string;
};

function summarizeError(error: unknown): ErrorSummary {
  if (isRouteErrorResponse(error)) {
    const routeDetail =
      typeof error.data === "string"
        ? error.data
        : error.data
          ? JSON.stringify(error.data, null, 2)
          : undefined;

    return {
      status: String(error.status),
      detail: [error.statusText, routeDetail].filter(Boolean).join("\n"),
    };
  }

  if (error instanceof Error) {
    return {
      status: "500",
      detail: error.stack ?? error.message,
    };
  }

  return { status: "500" };
}

function createReferenceId() {
  const suffix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);

  return `OURS-${suffix.toUpperCase()}`;
}

export default function ErrorPage({ error, onRetry }: ErrorPageProps) {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [{ status, detail }] = useState(() => summarizeError(error));
  const [referenceId] = useState(createReferenceId);
  const [occurredAt] = useState(() => new Date().toISOString());

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const retry = () => {
    if (onRetry) {
      onRetry();
      return;
    }

    window.location.reload();
  };

  const diagnosticText = [
    `Reference: ${referenceId}`,
    `Status: ${status}`,
    `Time: ${occurredAt}`,
    `URL: ${window.location.href}`,
    ...(import.meta.env.DEV && detail ? [`Detail:\n${detail}`] : []),
  ].join("\n");

  const copyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(diagnosticText);
      toast.success(t("error.copied"));
    } catch {
      toast.error(t("error.copyFailed"));
    }
  };

  return (
    <main className="min-h-dvh bg-background px-4 py-[max(1rem,env(safe-area-inset-top))] font-sans text-foreground sm:px-6 lg:px-10">
      <p className="sr-only" role="alert">
        {t("error.unexpectedTitle")} {t("error.unexpectedDescription")}
      </p>
      <div className="mx-auto flex min-h-[calc(100dvh-2rem)] w-full max-w-[68rem] flex-col">
        <header className="flex min-h-16 items-center border-b border-border py-3">
          <a
            aria-label={t("error.oursHome")}
            className="inline-flex min-h-11 items-center rounded-sm transition-transform duration-[var(--duration-fast)] ease-out-expo active:scale-[0.97] focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
            href="/"
          >
            <BrandLogo />
          </a>
          <span className="ml-auto font-mono text-label font-bold tracking-label text-muted uppercase">
            {t("error.systemStatus")}
          </span>
        </header>

        <div className="grid flex-1 content-center gap-8 py-10 md:grid-cols-[minmax(0,1.1fr)_minmax(18rem,0.9fr)] md:items-center md:gap-12 lg:gap-20">
          <section className="page-motion min-w-0">
            <p className="m-0 font-mono text-label font-bold tracking-label text-primary uppercase">
              {t("error.unexpectedEyebrow", { status })}
            </p>
            <h1
              ref={headingRef}
              className="mt-4 mb-0 max-w-[12ch] text-[clamp(2.75rem,7vw,5.75rem)] leading-[0.94] font-extrabold tracking-display text-balance outline-none"
              tabIndex={-1}
            >
              {t("error.unexpectedTitle")}
            </h1>
            <p className="mt-6 mb-0 max-w-[35rem] text-body leading-relaxed text-muted">
              {t("error.unexpectedDescription")}
            </p>

            <div className="mt-8 flex flex-col gap-3 xs:flex-row xs:flex-wrap">
              <button
                className={cn(
                  primaryButtonStyles,
                  "inline-flex items-center justify-center gap-2 xs:min-w-36",
                )}
                type="button"
                onClick={retry}
              >
                <ArrowClockwise aria-hidden="true" size={18} weight="bold" />
                {t("error.tryAgain")}
              </button>
              <a
                className={cn(
                  secondaryButtonStyles,
                  "inline-flex items-center justify-center gap-2 xs:min-w-36",
                )}
                href="/"
              >
                <House aria-hidden="true" size={18} weight="bold" />
                {t("error.backToExplore")}
              </a>
            </div>
          </section>

          <aside className="page-motion min-w-0 [animation-delay:50ms]">
            <div className="overflow-hidden rounded-card border border-border bg-surface shadow-card">
              <div className="flex items-start justify-between gap-6 border-b border-border p-5 sm:p-6">
                <span className="grid size-12 shrink-0 place-items-center rounded-full bg-[color-mix(in_srgb,var(--danger)_12%,transparent)] text-danger">
                  <WarningCircle aria-hidden="true" size={26} weight="fill" />
                </span>
                <span
                  aria-label={t("error.statusCode", { status })}
                  className="font-mono text-[clamp(3.5rem,11vw,6.5rem)] leading-[0.8] font-bold tracking-[-0.08em] text-foreground"
                >
                  {status}
                </span>
              </div>

              <div className="p-5 sm:p-6">
                <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-body-sm">
                  <dt className="font-mono font-bold tracking-label text-muted uppercase">
                    {t("error.reference")}
                  </dt>
                  <dd className="m-0 min-w-0 text-right font-mono font-bold break-all tabular-nums">
                    {referenceId}
                  </dd>
                  <dt className="font-mono font-bold tracking-label text-muted uppercase">
                    {t("error.time")}
                  </dt>
                  <dd className="m-0 min-w-0 text-right font-mono text-muted tabular-nums">
                    {occurredAt}
                  </dd>
                </dl>

                <details className="group mt-5 border-t border-border pt-1">
                  <summary className="flex min-h-11 cursor-pointer list-none items-center font-bold text-foreground marker:hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
                    {t("error.technicalDetails")}
                    <span
                      aria-hidden="true"
                      className="ml-auto transition-transform duration-[var(--duration-fast)] ease-out-expo group-open:rotate-45"
                    >
                      +
                    </span>
                  </summary>
                  <div className="pt-3">
                    <pre className="m-0 max-h-56 overflow-auto rounded-sm bg-surface-muted p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap text-muted">
                      {diagnosticText}
                    </pre>
                    <button
                      className={cn(
                        secondaryButtonStyles,
                        "mt-3 inline-flex w-full items-center justify-center gap-2",
                      )}
                      type="button"
                      onClick={copyDiagnostics}
                    >
                      <Copy aria-hidden="true" size={17} weight="bold" />
                      {t("error.copyDetails")}
                    </button>
                  </div>
                </details>
              </div>
            </div>
          </aside>
        </div>

        <footer className="border-t border-border py-5 text-body-sm text-muted">
          {t("error.supportHint", { reference: referenceId })}
        </footer>
      </div>
    </main>
  );
}
